import React, { useEffect, useState } from "react";
import { Modal, ModalDialog, Button, Checkbox } from "@mui/joy";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postFetch } from "../../../../../fetch/fetch";

/**
 * 결제관리 > 신규 결제링크 > 윈터 결제링크 탭 > '취소하기' 팝업
 *  - 카드(결제선생): 서버가 결제선생에 취소(환불) 요청 → 성공하면 결제 기록 취소(canceled=1)·잔액 0이면 수강권 취소·링크 isPaid=0
 *  - 계좌이체: 환불은 직원이 직접. 서버는 기록만 취소 처리
 *  - 결제선생 사이트에서 이미 취소한 경우 체크박스(dbOnly) → 카드도 DB 만 정리
 *  POST /dashboard/payments/managepayments/cancelwinterpayment { newPayLinkId, dbOnly }
 * props: open, onClose, row(표 행: id, name, phoneNumber, location, price, paymentMethod, paymentMethodRaw), onDone()
 */

const toastOptions: any = {
    position: "bottom-right",
    autoClose: 3000,
    hideProgressBar: true,
    theme: "colored",
};

const WinterPayCancelModal: React.FC<any> = (props) => {

    const [dbOnly, setDbOnly] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [failDetail, setFailDetail] = useState<string>("");

    useEffect(() => {
        if (props.open) {
            setDbOnly(false);
            setLoading(false);
            setFailDetail("");
        }
    }, [props.open]);

    const row = props.row;
    if (!row) return null;

    const isCard = row.paymentMethodRaw === "payteacher";

    const submit = async () => {
        if (loading) return;
        try {
            setLoading(true);
            setFailDetail("");
            const result: any = await postFetch("/dashboard/payments/managepayments/cancelwinterpayment", {
                newPayLinkId: row.id,
                dbOnly: isCard ? dbOnly : false,
            });
            if (result.message === "success") {
                toast.success(`${row.name} 결제를 취소 처리했습니다${result.orderItemCanceled ? " (수강권도 취소됨)" : ""}`, toastOptions);
                props.onDone && props.onDone();
                props.onClose && props.onClose();
            } else if (result.message === "fail") {
                setFailDetail(result.detail || "결제선생 취소 요청에 실패했습니다");
            } else if (result.message === "notPaid") {
                toast.error("취소할 결제 기록이 없습니다 (이미 취소됐거나 미결제)", toastOptions);
                props.onDone && props.onDone();
                props.onClose && props.onClose();
            } else {
                console.log(result);
                toast.error("취소 처리에 실패했습니다. 다시 시도해 주세요.", toastOptions);
            }
        } catch (e) {
            console.log(e);
            toast.error("취소 처리에 실패했습니다. 다시 시도해 주세요.", toastOptions);
        } finally {
            setLoading(false);
        }
    };

    const line: React.CSSProperties = { display: "flex", justifyContent: "space-between", fontSize: "13px", padding: "5px 0", borderBottom: "1px solid #F2F4F6" };
    const key: React.CSSProperties = { color: "#6B7684" };

    return (
        <Modal open={!!props.open} onClose={loading ? undefined : props.onClose}>
            <ModalDialog>
                <div style={{ width: "380px", maxWidth: "92vw" }}>
                    <div style={{ fontSize: "18px", fontWeight: 600, marginBottom: "6px" }}>
                        윈터 결제 취소
                    </div>
                    <div style={{ fontSize: "13px", color: "#6B7684", marginBottom: "16px" }}>
                        {isCard
                            ? "결제선생에 취소(환불) 요청을 보내고, 결제 기록과 수강권을 취소 처리합니다."
                            : "계좌이체 건은 환불을 직접 해주세요. 여기서는 결제 기록과 수강권만 취소 처리합니다."}
                    </div>

                    <div style={{ marginBottom: "16px" }}>
                        <div style={line}><span style={key}>이름</span><span>{row.name}{row.studentName && row.studentName !== row.name ? ` (학생 ${row.studentName})` : ""}</span></div>
                        <div style={line}><span style={key}>전화번호</span><span>{row.phoneNumber}</span></div>
                        <div style={line}><span style={key}>지점</span><span>{row.location}</span></div>
                        <div style={line}><span style={key}>등록기간</span><span>{row.date}</span></div>
                        <div style={line}><span style={key}>금액</span><span>{row.price}원</span></div>
                        <div style={{ ...line, borderBottom: "none" }}><span style={key}>결제수단</span><span>{row.paymentMethod}</span></div>
                    </div>

                    {isCard && (
                        <Checkbox
                            size="sm"
                            label="결제선생 사이트에서 이미 취소함 (DB만 정리)"
                            checked={dbOnly}
                            onChange={(e) => setDbOnly(e.target.checked)}
                            disabled={loading}
                            sx={{ marginBottom: "12px" }}
                        />
                    )}

                    {failDetail && (
                        <div style={{ fontSize: "12px", color: "#E8505B", background: "#FFF1F2", borderRadius: "6px", padding: "8px 10px", marginBottom: "12px", lineHeight: 1.5 }}>
                            결제선생 취소 실패: {failDetail}
                            <br />결제선생 사이트에서 직접 취소한 뒤, 위 체크박스를 켜고 다시 눌러 DB만 정리할 수 있어요.
                        </div>
                    )}

                    <div style={{ fontSize: "12px", color: "#E8505B", fontWeight: 600, marginBottom: "16px" }}>
                        되돌릴 수 없습니다. 취소 후 이 링크는 다시 "미결제" 상태가 되고 고객이 같은 링크로 재결제할 수 있습니다. 취소 알림톡은 보내지 않습니다.
                    </div>

                    <div style={{ display: "flex", gap: "8px" }}>
                        <Button variant="outlined" color="neutral" fullWidth onClick={props.onClose} disabled={loading}>
                            닫기
                        </Button>
                        <Button variant="solid" color="danger" fullWidth onClick={submit} loading={loading}>
                            {isCard && !dbOnly ? "결제선생 취소 + 기록 취소" : "기록 취소 처리"}
                        </Button>
                    </div>
                </div>
            </ModalDialog>
        </Modal>
    );
};

export default WinterPayCancelModal;
